import React from 'react';
import { useHistory } from 'react-router-dom';
import { Auth } from 'aws-amplify';

const pageContainer: React.CSSProperties = {
  minHeight: '100vh',
  backgroundColor: '#261136',
  padding: 24,
  boxSizing: 'border-box',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  fontFamily:
    'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
};

const shellCard: React.CSSProperties = {
  width: '100%',
  maxWidth: 460,
  background:
    'radial-gradient(circle at top, #3A1F52 0, #170824 45%, #12061f 100%)',
  borderRadius: 16,
  padding: 24,
  boxSizing: 'border-box',
  boxShadow: '0 18px 45px rgba(0,0,0,0.45)',
  color: '#F5F5FA',
};

const headerBlock: React.CSSProperties = {
  marginBottom: 20,
};

const titleStyle: React.CSSProperties = {
  fontSize: 22,
  fontWeight: 600,
};

const subtitleStyle: React.CSSProperties = {
  fontSize: 14,
  opacity: 0.8,
  marginTop: 4,
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 13,
  marginBottom: 4,
  opacity: 0.9,
};

const inputWrapper: React.CSSProperties = {
  marginBottom: 14,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '9px 11px',
  borderRadius: 8,
  border: '1px solid rgba(255,255,255,0.14)',
  backgroundColor: 'rgba(10, 4, 20, 0.9)',
  color: '#F5F5FA',
  fontSize: 14,
  boxSizing: 'border-box',
  outline: 'none',
} as const;

const helperRow: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: 4,
};

const helperText: React.CSSProperties = {
  fontSize: 12,
  opacity: 0.75,
};

const pillButton: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: 999,
  backgroundColor: '#7B1FA2',
  color: '#fff',
  border: 'none',
  cursor: 'pointer',
  fontSize: 15,
  fontWeight: 500,
  marginTop: 6,
};

const pillButtonDisabled: React.CSSProperties = {
  ...pillButton,
  opacity: 0.6,
  cursor: 'default',
};

const errorBox: React.CSSProperties = {
  fontSize: 12,
  color: '#FFB3C1',
  backgroundColor: 'rgba(255, 82, 82, 0.12)',
  borderRadius: 8,
  padding: '8px 10px',
  marginTop: 4,
  marginBottom: 4,
  border: '1px solid rgba(255,82,82,0.25)',
};

const successBox: React.CSSProperties = {
  fontSize: 12,
  color: '#C4F8C2',
  backgroundColor: 'rgba(76, 175, 80, 0.12)',
  borderRadius: 8,
  padding: '8px 10px',
  marginTop: 4,
  marginBottom: 4,
  border: '1px solid rgba(76,175,80,0.25)',
};

const smallCaption: React.CSSProperties = {
  fontSize: 11,
  opacity: 0.65,
  marginTop: 12,
  textAlign: 'center',
};

const backLink: React.CSSProperties = {
  fontSize: 12,
  textAlign: 'center',
  marginTop: 10,
  opacity: 0.8,
  textDecoration: 'underline',
  cursor: 'pointer',
};

const TenantPasswordResetPage: React.FC = () => {
  const history = useHistory();

  const [email, setEmail] = React.useState('');
  const [code, setCode] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [passwordReentry, setPasswordReentry] = React.useState('');

  const [confirmationCodeSent, setConfirmationCodeSent] = React.useState(false);

  const [validPassword, setValidPassword] = React.useState(false);
  const [validPasswordUI, setValidPasswordUI] = React.useState(true);
  const [validPasswordReentry, setValidPasswordReentry] =
    React.useState(true);
  const [validInput, setValidInput] = React.useState(false);

  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);

  // same rules as mobile: >= 8 chars and contains a number
  const checkPasswordInput = React.useCallback(() => {
    const hasNumberRegex = /\d/;
    if (password.length >= 8 && hasNumberRegex.test(password)) {
      setValidPassword(true);
      setValidPasswordUI(true);
    } else {
      setValidPassword(false);
      setValidPasswordUI(false);
    }
  }, [password]);

  const checkPasswordReentry = React.useCallback(() => {
    if (!password || password !== passwordReentry) {
      setValidPasswordReentry(false);
    } else {
      setValidPasswordReentry(true);
    }
  }, [password, passwordReentry]);

  const checkValidInput = React.useCallback(() => {
    if (validPassword && validPasswordReentry) {
      setValidInput(true);
    } else {
      setValidInput(false);
    }
  }, [validPassword, validPasswordReentry]);

  // keep validity in sync when fields change
  React.useEffect(() => {
    if (!confirmationCodeSent) return;
    checkPasswordInput();
    checkPasswordReentry();
    checkValidInput();
  }, [
    password,
    passwordReentry,
    confirmationCodeSent,
    checkPasswordInput,
    checkPasswordReentry,
    checkValidInput,
  ]);

  const handleRequestCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      const trimmedEmail = email.trim().toLowerCase();
      if (!trimmedEmail) {
        throw new Error('Please enter the email attached to your account.');
      }

      await Auth.forgotPassword(trimmedEmail);
      setConfirmationCodeSent(true);
      setSuccess('A confirmation code has been sent to your email.');
    } catch (err: any) {
      console.error('Error sending reset code: ', err);
      setError(err.message || 'Unable to send reset code.');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    // run validations
    checkPasswordInput();
    checkPasswordReentry();
    checkValidInput();

    if (!validPassword || !validPasswordReentry) {
      setError('Please fix the password errors before continuing.');
      return;
    }

    setLoading(true);
    try {
      const trimmedEmail = email.trim().toLowerCase();
      await Auth.forgotPasswordSubmit(trimmedEmail, code.trim(), password);

      setSuccess('Your password has been reset. You can now sign in.');
      // optional redirect after short delay, or immediately:
      history.push('/tenant/login');
    } catch (err: any) {
      console.error('Error confirming new password: ', err);
      setError(err.message || 'Unable to reset password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={pageContainer}>
      <div style={shellCard}>
        <div style={headerBlock}>
          <h1 style={titleStyle}>
            {confirmationCodeSent ? 'Set a new password' : 'Password recovery'}
          </h1>
          <p style={subtitleStyle}>
            {confirmationCodeSent
              ? 'Enter the code we emailed you and choose a new password.'
              : 'Enter the email attached to your account to receive a reset code.'}
          </p>
        </div>

        {!confirmationCodeSent ? (
          <form onSubmit={handleRequestCode}>
            <div style={inputWrapper}>
              <label style={labelStyle}>Email</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                style={inputStyle}
                placeholder="you@example.com"
              />
              <div style={helperRow}>
                <span style={helperText}>
                  We&apos;ll send a one-time code to this email.
                </span>
              </div>
            </div>

            {error && <div style={errorBox}>{error}</div>}
            {success && <div style={successBox}>{success}</div>}

            <button
              type="submit"
              disabled={loading}
              style={loading ? pillButtonDisabled : pillButton}
            >
              {loading ? 'Sending code…' : 'Send reset code'}
            </button>

            <div style={backLink} onClick={() => history.push('/tenant/login')}>
              Back to login
            </div>
          </form>
        ) : (
          <form onSubmit={handleConfirmNewPassword}>
            <div style={inputWrapper}>
              <label style={labelStyle}>Confirmation code</label>
              <input
                type="text"
                value={code}
                onChange={e => setCode(e.target.value)}
                required
                style={inputStyle}
                placeholder="Enter the code from your email"
              />
            </div>

            <div style={inputWrapper}>
              <label
                style={{
                  ...labelStyle,
                  color: validPasswordUI ? '#F5F5FA' : '#FFB3C1',
                }}
              >
                New password{' '}
                {!validPasswordUI && '– Must be 8 characters and contain a number.'}
              </label>
              <input
                type="password"
                value={password}
                onChange={e => {
                  setPassword(e.target.value);
                }}
                required
                style={inputStyle}
                placeholder="Enter your new password"
              />
            </div>

            <div style={inputWrapper}>
              <label
                style={{
                  ...labelStyle,
                  color: validPasswordReentry ? '#F5F5FA' : '#FFB3C1',
                }}
              >
                Re-enter new password{' '}
                {!validPasswordReentry && '– Passwords do not match.'}
              </label>
              <input
                type="password"
                value={passwordReentry}
                onChange={e => {
                  setPasswordReentry(e.target.value);
                }}
                required
                style={inputStyle}
                placeholder="Re-enter your new password"
              />
            </div>

            {error && <div style={errorBox}>{error}</div>}
            {success && <div style={successBox}>{success}</div>}

            <button
              type="submit"
              disabled={loading}
              style={loading ? pillButtonDisabled : pillButton}
            >
              {loading ? 'Updating password…' : 'Confirm new password'}
            </button>

            <div style={smallCaption}>
              If you didn&apos;t get a code, go back and double-check your email
              address.
            </div>

            <div style={backLink} onClick={() => setConfirmationCodeSent(false)}>
              Back to email step
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default TenantPasswordResetPage;
